import { Button, Text, Toast } from '@belong/ui';
import {
  MultiselectDateTimeRangePicker,
  DateTimeSelection,
} from 'accounts/components/multiselect-date-time-range-picker/multiselect-date-time-range-picker';
import { STRINGS } from 'accounts/constants/strings/reschedule-appointment';
import { useRescheduleAppointmentContext } from 'accounts/hooks/reschedule-appointment';
import type { RescheduleAppointmentOptionsFormSchema } from 'accounts/types';
import { getDateTimeValidationState } from 'accounts/utils/date-time-validation';
import LegacyForm from 'components/Form/Form';
import { QuestionsOrConcernsModalFooter } from 'post-inspection/components/question-or-concern-modal-footer/question-or-concern-modal-footer';
import { template } from '@belong/common';
import { useState, useEffect } from 'react';

const Form = LegacyForm;

type Props = {
  onSubmit: (values: RescheduleAppointmentOptionsFormSchema) => Promise<void>;
};

export function RescheduleInspectionAppointmentOptionsForm({
  onSubmit,
}: Props) {
  const { timeZone, unit, isHandledByAnExternalProvider } =
    useRescheduleAppointmentContext();
  const [selectedSelections, setSelectedSelections] = useState<
    DateTimeSelection[]
  >([]);
  const [isValidationToastOpen, setIsValidationToastOpen] = useState(false);
  const [showDateValidationErrors, setShowDateValidationErrors] =
    useState(false);
  const [showTimeValidationErrors, setShowTimeValidationErrors] =
    useState(false);
  // `undefined` is reachable: the picker's "remove all selections for this date"
  // button calls `onChange` with one argument.
  const [selectedDate, setSelectedDate] = useState<string | null | undefined>(
    null,
  );

  // Hide validation toast and reset submit attempt when selections are made
  useEffect(() => {
    const config = { minSelections: 3, isHandledByAnExternalProvider };
    const validationResult = getDateTimeValidationState(
      selectedSelections,
      selectedDate,
      config,
    );

    if (!validationResult.shouldShowToast) {
      setIsValidationToastOpen(false);
      setShowDateValidationErrors(false);
      setShowTimeValidationErrors(false);
    }
  }, [selectedSelections, selectedDate, isHandledByAnExternalProvider]);

  function handleValidationToastClose() {
    setIsValidationToastOpen(false);
  }

  function handleSubmitClick() {
    const config = { minSelections: 3, isHandledByAnExternalProvider };
    const validationResult = getDateTimeValidationState(
      selectedSelections,
      selectedDate,
      config,
    );

    if (validationResult.shouldShowToast) {
      setIsValidationToastOpen(true);
      setShowDateValidationErrors(
        validationResult.validationState.showDateValidationErrors,
      );
      setShowTimeValidationErrors(
        validationResult.validationState.showTimeValidationErrors,
      );
      return false; // Prevents form submission
    }

    return true;
  }

  return (
    <>
      <Form
        id="reschedule-inspection-form"
        initialValues={{}}
        onSubmit={onSubmit}
        allowSubmitErrors={false}
        customError={null}
        getFormBottomBar={() => null}
        getForm={({ values, form }: { values: any; form: any }) => {
          const { startTimeOptions: _startTimeOptions } =
            values as RescheduleAppointmentOptionsFormSchema;

          const minRequired = isHandledByAnExternalProvider ? 1 : 3;
          const isSubmitButtonDisabled =
            selectedSelections.length < minRequired;

          return (
            <div className="w-full">
              <div className="px-2sm sm:px-0">
                <div className="mb-xs px-0 sm:px-sm">
                  <Text fontWeight="semibold" variant="h3">
                    {STRINGS['step.options.reschedule.title']}
                  </Text>
                </div>
                <div className="mb-xl px-0 sm:px-sm">
                  <Text className="text-dark-gray">
                    {STRINGS['step.options.reschedule.subtitle']}
                  </Text>
                </div>
                <div className="mb-2xl lg:px-2sm">
                  <MultiselectDateTimeRangePicker
                    unitId={String(unit?.basicInfo?.id || '')}
                    timeZone={timeZone}
                    title={template(
                      STRINGS['step.options.reschedule.calendar_instruction'],
                      {
                        count: isHandledByAnExternalProvider ? 1 : 3,
                        timesLabel: isHandledByAnExternalProvider
                          ? 'Time'
                          : 'Times',
                      },
                    )}
                    minSelections={isHandledByAnExternalProvider ? 1 : 3}
                    maxSelections={isHandledByAnExternalProvider ? 1 : 10}
                    showDateValidationErrors={showDateValidationErrors}
                    showTimeValidationErrors={showTimeValidationErrors}
                    onDateValidationReset={() =>
                      setShowDateValidationErrors(false)
                    }
                    onTimeValidationReset={() =>
                      setShowTimeValidationErrors(false)
                    }
                    onChange={(selections, selectedDate) => {
                      // Store the full selections for validation
                      setSelectedSelections(selections);
                      setSelectedDate(selectedDate);

                      if (selections && selections.length > 0) {
                        // Convert all selections to startTimeOptions array
                        const allTimeOptions: string[] = [];
                        selections.forEach((selection) => {
                          allTimeOptions.push(...selection.availableTimes);
                        });

                        // Remove duplicates and sort
                        const uniqueTimeOptions = [
                          ...new Set(allTimeOptions),
                        ].sort();

                        form.change('startTimeOptions', uniqueTimeOptions);

                        // Keep the first selection for backward compatibility.
                        // Guarded by the `selections.length > 0` check above.
                        const firstSelection = selections[0]!;
                        const selectedDate = new Date(firstSelection.date);
                        const selectedTime = firstSelection.availableTimes[0];
                        form.change('date', selectedDate);
                        form.change('time', selectedTime);
                      } else {
                        form.change('startTimeOptions', []);
                      }
                    }}
                  />
                </div>
                <QuestionsOrConcernsModalFooter>
                  <div className="w-full sm:w-button-default">
                    <Button
                      disabled={isSubmitButtonDisabled}
                      type="submit"
                      onClick={(e) => {
                        if (!handleSubmitClick()) {
                          e.preventDefault();
                        }
                      }}
                    >
                      <Text fontWeight="semibold">
                        {STRINGS['step.options.option.form.title']}
                      </Text>
                    </Button>
                  </div>
                </QuestionsOrConcernsModalFooter>
                <div className="h-2xl md:h-0" />
              </div>
            </div>
          );
        }}
      />
      <Toast
        isVisible={isValidationToastOpen}
        autocloseDuration={5000}
        variant="danger"
        onClose={handleValidationToastClose}
      >
        Please select{' '}
        {isHandledByAnExternalProvider ? '1 time' : 'at least 3 times'}
      </Toast>
    </>
  );
}
